// Pure Location Clustering module.
//
// Turns the free-text places people type ("Ithaca, NY", "NYC", "Bay Area")
// into stable cluster keys so the network map can seat people from the same
// place together. No React, no Firebase, no D3.
//
// Two levels of grouping:
//   clusterKeyFor  — the place itself (metro-normalized): one sector on the map
//   regionKeyFor   — the state: used only to ORDER sectors, so that nearby
//                    places end up as neighbouring wedges rather than scattered

import { US_STATES } from "../constants/usStates";
import { contactProfileForEntry } from "./privateContactProfile";

const STATE_SET = new Set(US_STATES.map((code) => code.toLowerCase()));

const STATE_NAMES = Object.freeze({
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", "district of columbia": "DC",
  florida: "FL", georgia: "GA", hawaii: "HI", idaho: "ID", illinois: "IL",
  indiana: "IN", iowa: "IA", kansas: "KS", kentucky: "KY", louisiana: "LA",
  maine: "ME", maryland: "MD", massachusetts: "MA", michigan: "MI",
  minnesota: "MN", mississippi: "MS", missouri: "MO", montana: "MT",
  nebraska: "NE", nevada: "NV", "new hampshire": "NH", "new jersey": "NJ",
  "new mexico": "NM", "new york": "NY", "north carolina": "NC",
  "north dakota": "ND", ohio: "OH", oklahoma: "OK", oregon: "OR",
  pennsylvania: "PA", "rhode island": "RI", "south carolina": "SC",
  "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT",
  vermont: "VT", virginia: "VA", washington: "WA", "west virginia": "WV",
  wisconsin: "WI", wyoming: "WY",
});

// Places people write that mean the same metro. Deliberately small and
// explicit — guessing at geography is worse than leaving a place on its own.
const METRO_ALIASES = Object.freeze({
  "nyc": "New York, NY",
  "new york city": "New York, NY",
  "new york": "New York, NY",
  "manhattan": "New York, NY",
  "brooklyn": "New York, NY",
  "queens": "New York, NY",
  "the bronx": "New York, NY",
  "bronx": "New York, NY",
  "sf": "San Francisco, CA",
  "san francisco": "San Francisco, CA",
  "bay area": "San Francisco, CA",
  "sf bay area": "San Francisco, CA",
  "silicon valley": "San Francisco, CA",
  "palo alto": "San Francisco, CA",
  "mountain view": "San Francisco, CA",
  "la": "Los Angeles, CA",
  "los angeles": "Los Angeles, CA",
  "dc": "Washington, DC",
  "washington dc": "Washington, DC",
  "washington d.c.": "Washington, DC",
  "boston": "Boston, MA",
  "cambridge": "Boston, MA",
  "somerville": "Boston, MA",
  "chicago": "Chicago, IL",
  "philly": "Philadelphia, PA",
  "philadelphia": "Philadelphia, PA",
});

function squash(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[.]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function titleCase(value) {
  return value
    .split(" ")
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(" ");
}

/**
 * Canonical "City, ST" for a free-text place, or "" when there is nothing
 * usable. Metro aliases win; otherwise the state half is normalized to its
 * two-letter code and the city half is title-cased.
 */
export function normalizeLocationLabel(raw) {
  const squashed = squash(raw);
  if (!squashed) return "";

  const withoutCountry = squashed.replace(/,?\s*(usa|united states|us)$/, "").trim();
  if (!withoutCountry) return "";
  if (METRO_ALIASES[withoutCountry]) return METRO_ALIASES[withoutCountry];

  const parts = withoutCountry.split(",").map((part) => part.trim()).filter(Boolean);
  const city = parts[0] || "";
  const rest = parts.slice(1).join(" ").trim();

  if (METRO_ALIASES[city] && (!rest || STATE_SET.has(rest) || STATE_NAMES[rest])) {
    return METRO_ALIASES[city];
  }

  let state = "";
  if (STATE_SET.has(rest)) state = rest.toUpperCase();
  else if (STATE_NAMES[rest]) state = STATE_NAMES[rest];

  if (!city) return "";
  return state ? `${titleCase(city)}, ${state}` : titleCase(city);
}

/** Stable grouping key for a place. Empty when the place is unusable. */
export function clusterKeyFor(raw) {
  return normalizeLocationLabel(raw).toLowerCase();
}

/** The state, used to order sectors so nearby places sit side by side. */
export function regionKeyFor(raw) {
  const label = normalizeLocationLabel(raw);
  const parts = label.split(",");
  return parts.length > 1 ? parts[1].trim() : "";
}

/**
 * Best-known current place for a rolodex entry.
 *
 * Prefers the Locations list (the richer, newer data, current entry first),
 * then the contact card's city/state pair, then its free-text location.
 */
export function currentLocationOf(entry, contactProfile = {}) {
  const locations = Array.isArray(entry?.locations) ? entry.locations : [];
  const current = locations.find((item) => item?.current) || locations[0];
  if (current?.label) return current.label;

  const city = contactProfile.city || entry?.private_profile?.city;
  const state = contactProfile.state || entry?.private_profile?.state;
  if (city && state) return `${city}, ${state}`;

  return contactProfile.location || entry?.private_profile?.location || "";
}

/** The place a graph node should be seated by. */
export function locationOfNode(node) {
  if (node?.location) return node.location;
  const profile = node?.profile || {};
  if (profile.city && profile.state) return `${profile.city}, ${profile.state}`;
  return profile.location || "";
}

export const UNPLACED_KEY = "__unplaced__";

/**
 * Group nodes into location clusters and assign each a sector of the circle.
 *
 * Sectors are ordered by state then city, so neighbouring wedges are
 * geographically related. People with no usable location share one trailing
 * sector and are never given a label.
 *
 * A cluster must contain at least `minClusterSize` people to be worth drawing;
 * smaller ones fall back into the unplaced sector, because a "cluster" of one
 * is just a node with a label stuck to it.
 */
export function buildLocationClusters(nodes = [], { minClusterSize = 2 } = {}) {
  const placeable = nodes.filter((node) => node && node.degree !== 0);

  const byKey = new Map();
  placeable.forEach((node) => {
    const key = clusterKeyFor(locationOfNode(node));
    if (!key) return;
    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        label: normalizeLocationLabel(locationOfNode(node)),
        region: regionKeyFor(locationOfNode(node)),
        nodeIds: [],
      });
    }
    byKey.get(key).nodeIds.push(node.id);
  });

  const kept = [...byKey.values()]
    .filter((cluster) => cluster.nodeIds.length >= minClusterSize)
    .sort((a, b) => {
      if (a.region !== b.region) return a.region < b.region ? -1 : 1;
      return a.label < b.label ? -1 : 1;
    });

  const assigned = new Set(kept.flatMap((cluster) => cluster.nodeIds));
  const unplacedIds = placeable
    .filter((node) => !assigned.has(node.id))
    .map((node) => node.id);

  const clusters = [...kept];
  if (unplacedIds.length) {
    clusters.push({
      key: UNPLACED_KEY,
      label: "",
      region: "￿",
      nodeIds: unplacedIds,
    });
  }

  // Give every cluster a wedge proportional to how many people it holds, so a
  // big city does not get squeezed into the same slice as a pair.
  const total = clusters.reduce((sum, cluster) => sum + cluster.nodeIds.length, 0) || 1;
  let cursor = -Math.PI / 2; // start at the top of the circle
  const byNodeId = new Map();

  clusters.forEach((cluster) => {
    const span = (cluster.nodeIds.length / total) * Math.PI * 2;
    cluster.startAngle = cursor;
    cluster.endAngle = cursor + span;
    cluster.angle = cursor + span / 2;
    cursor += span;

    cluster.nodeIds.forEach((id, index) => {
      // Spread members across their own wedge instead of stacking them on the
      // centre line, leaving a margin so adjacent clusters stay distinct.
      const fraction = cluster.nodeIds.length === 1
        ? 0.5
        : (index + 0.5) / cluster.nodeIds.length;
      const inset = span * 0.12;
      byNodeId.set(id, {
        cluster,
        angle: cluster.startAngle + inset + fraction * (span - inset * 2),
      });
    });
  });

  return {
    clusters,
    byNodeId,
    // Below two real clusters there is nothing to group, and forcing angles
    // would only make a small graph look arbitrary.
    isMeaningful: kept.length >= 2,
  };
}

/** Where the owner believes one person lives. The single source for both maps. */
export function locationForEntry(entry) {
  return currentLocationOf(entry, contactProfileForEntry(entry));
}

/**
 * The owner's own record of where each person in their rolodex lives, keyed by
 * every id a graph node might carry (registered uid and private entry id).
 *
 * Both maps resolve locations through this index so the dashboard and Search
 * always seat the same person in the same place. The Discovery Graph otherwise
 * only knows a person's *published* location, which can differ from what the
 * owner has recorded privately — that difference used to leak into the layout.
 *
 * This reads only the owner's own entries. No other user's private data is
 * involved, and nothing here is published or used for search matching.
 */
export function knownLocationIndex(entries = []) {
  const index = new Map();
  entries.forEach((entry) => {
    if (!entry) return;
    const location = locationForEntry(entry);
    if (!location) return;
    if (entry.recipient_uid) index.set(entry.recipient_uid, location);
    if (entry.id) index.set(entry.id, location);
  });
  return index;
}

/**
 * Seat a set of graph nodes using the owner's own locations where known,
 * falling back to whatever the node already carries.
 */
export function withKnownLocations(nodes = [], index = new Map()) {
  if (!index.size) return nodes;
  return nodes.map((node) => {
    if (!node?.id) return node;
    const known = index.get(node.id) || index.get(node.entryId);
    if (!known || known === node.location) return node;
    return { ...node, location: known };
  });
}
